import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
    Store, User, Wallet, Clock, CheckCircle, Loader2,
    AlertTriangle, Lock, Unlock
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const RegisterOpeningDialog = ({
    open,
    onOpenChange,
    registers = [],
    users = [],
    currentUser,
    onOpen,
    api
}) => {
    const [selectedRegister, setSelectedRegister] = useState('');
    const [selectedUser, setSelectedUser] = useState('');
    const [openingCash, setOpeningCash] = useState('');
    const [loading, setLoading] = useState(false);
    
    // Reset on open
    useEffect(() => {
        if (open) {
            setSelectedRegister('');
            setSelectedUser(currentUser?.id || '');
            setOpeningCash('');
        }
    }, [open, currentUser]);
    
    // Filter available registers (closed and active)
    const availableRegisters = registers.filter(r => 
        r.is_active && (r.status === 'closed' || !r.status)
    );
    
    const selectedReg = registers.find(r => r.id === selectedRegister);
    
    const handleSubmit = async () => {
        if (!selectedRegister) {
            toast.error('Veuillez sélectionner une caisse');
            return;
        }
        
        const opening = parseFloat(openingCash) || 0;
        if (opening < 0) {
            toast.error('Le fond de caisse ne peut pas être négatif');
            return;
        }
        
        setLoading(true);
        try {
            await onOpen({
                register_id: selectedRegister,
                user_id: selectedUser || currentUser?.id,
                opening_cash: opening,
                opened_at: new Date().toISOString()
            });
            onOpenChange(false);
        } catch (error) {
            console.error('Opening error:', error);
            toast.error(error.message || 'Erreur lors de l\'ouverture');
        } finally {
            setLoading(false);
        }
    };
    
    const formatAmount = (amount) => {
        return new Intl.NumberFormat('fr-FR').format(amount) + ' F';
    };
    
    const getRegisterIcon = (type) => {
        if (type === 'bar') return '🍺';
        if (type === 'admin') return '👑';
        return '📋';
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3 text-xl">
                        <div className="p-2 rounded-lg bg-emerald-100">
                            <Unlock className="w-6 h-6 text-emerald-700" />
                        </div>
                        Ouverture de Caisse
                    </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 py-4">
                    {/* Register Selection */}
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                            <Store className="w-4 h-4" />
                            Sélectionner la caisse
                        </Label>
                        <Select value={selectedRegister} onValueChange={setSelectedRegister}>
                            <SelectTrigger className="h-12">
                                <SelectValue placeholder="Choisir une caisse..." />
                            </SelectTrigger>
                            <SelectContent>
                                {availableRegisters.length === 0 ? (
                                    <div className="p-4 text-center text-muted-foreground">
                                        <Lock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        <p>Aucune caisse disponible</p>
                                        <p className="text-xs">Toutes les caisses sont déjà ouvertes</p>
                                    </div>
                                ) : (
                                    availableRegisters.map(reg => (
                                        <SelectItem key={reg.id} value={reg.id}>
                                            <div className="flex items-center gap-3">
                                                <span>{getRegisterIcon(reg.type)}</span>
                                                <div>
                                                    <span className="font-medium">{reg.name}</span>
                                                    {reg.description && (
                                                        <span className="text-xs text-muted-foreground ml-2">
                                                            - {reg.description}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))
                                )}
                            </SelectContent>
                        </Select>
                        
                        {selectedReg && (
                            <div className={cn(
                                "p-3 rounded-lg border mt-2",
                                selectedReg.type === 'bar' ? "bg-amber-50 border-amber-200" :
                                selectedReg.type === 'admin' ? "bg-purple-50 border-purple-200" :
                                "bg-blue-50 border-blue-200"
                            )}>
                                <div className="flex items-center justify-between">
                                    <span className="font-medium">{selectedReg.name}</span>
                                    <Badge variant="outline">
                                        {selectedReg.type === 'bar' ? 'Bar' : 
                                         selectedReg.type === 'admin' ? 'Admin' : 'Standard'}
                                    </Badge>
                                </div>
                                {selectedReg.description && (
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {selectedReg.description}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {/* User Selection (for admin) */}
                    {currentUser?.role === 'ADMIN' && users.length > 0 && (
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Caissier
                            </Label>
                            <Select value={selectedUser} onValueChange={setSelectedUser}>
                                <SelectTrigger className="h-12">
                                    <SelectValue placeholder="Sélectionner l'utilisateur..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.map(user => (
                                        <SelectItem key={user.id} value={user.id}>
                                            <div className="flex items-center gap-2">
                                                <User className="w-4 h-4" />
                                                <span>{user.name || user.username}</span>
                                                <Badge variant="secondary" className="text-xs">
                                                    {user.role}
                                                </Badge>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    
                    {/* Opening Cash */}
                    <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                            <Wallet className="w-4 h-4" />
                            Fond de caisse (FCFA)
                        </Label>
                        <Input
                            type="number"
                            placeholder="0"
                            value={openingCash}
                            onChange={(e) => setOpeningCash(e.target.value)}
                            className="h-14 text-2xl font-mono text-center"
                        />
                        {openingCash && parseFloat(openingCash) > 0 && (
                            <p className="text-sm text-muted-foreground text-center">
                                Fond de caisse: <strong>{formatAmount(parseFloat(openingCash))}</strong>
                            </p>
                        )}
                    </div>
                    
                    {/* Time Info */}
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>Heure d'ouverture: {new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    
                    {/* Warning if no register selected */}
                    {availableRegisters.length === 0 && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                                <p className="font-medium text-amber-800">Toutes les caisses sont occupées</p>
                                <p className="text-amber-700">Vous devez d'abord clôturer une caisse pour pouvoir en ouvrir une nouvelle.</p>
                            </div>
                        </div>
                    )}
                </div>
                
                <DialogFooter>
                    <Button 
                        variant="outline" 
                        onClick={() => onOpenChange(false)} 
                        disabled={loading}
                        data-testid="register-opening-cancel-btn"
                    >
                        Annuler
                    </Button>
                    <Button 
                        onClick={handleSubmit} 
                        disabled={loading || !selectedRegister || availableRegisters.length === 0}
                        className="bg-emerald-600 hover:bg-emerald-700 min-w-[180px]"
                        data-testid="register-opening-confirm-btn"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Ouverture...
                            </>
                        ) : (
                            <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Ouvrir la Caisse
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default RegisterOpeningDialog;
